/**
 * Форматування сум і дат.
 *
 * Важливо: `formatShortDate` і `transformDate` формують дату у форматі
 * ISO (YYYY-MM-DD) — саме його очікує бекенд, тому їх чіпати не можна.
 * Для показу користувачеві використовуйте `displayDate` / `displayDateTime`.
 */

export const formatCurrency = (value, currency = 'UAH') => {
    const numericValue = Number(value);

    if (Number.isNaN(numericValue)) {
        return '—';
    }

    // Українська локаль для всіх валют: розділювач тисяч — нерозривний
    // пробіл, десятковий — кома. Раніше USD і EUR форматувалися за
    // en-US, і в одному стовпці сусідили «18 450,50 грн» та «$320.00».
    return new Intl.NumberFormat('uk-UA', {
        style: 'currency',
        currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(numericValue);
};

/** YYYY-MM-DD — формат, у якому дату приймає бекенд. */
export const transformDate = (date) => {
    const day = `0${date.getDate()}`.slice(-2);
    const month = `0${date.getMonth() + 1}`.slice(-2);

    return `${date.getFullYear()}-${month}-${day}`;
};

/** YYYY-MM-DD із будь-якого значення, яке розуміє Date. */
export const formatShortDate = (unformattedDate) => transformDate(new Date(unformattedDate));

/** YYYY-MM-DD HH:mm:ss — залишено для сумісності зі старим кодом. */
export const formatDate = (unformattedDate) => {
    const date = new Date(unformattedDate);

    if (Number.isNaN(date.getTime())) {
        return '';
    }

    const pad = (value) => String(value).padStart(2, '0');

    return (
        `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ` +
        `${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`
    );
};

/** ДД.ММ.РРРР — для показу касиру. */
export const displayDate = (value) => {
    const date = new Date(value);

    if (!value || Number.isNaN(date.getTime())) {
        return '—';
    }

    return new Intl.DateTimeFormat('uk-UA', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
    }).format(date);
};

/** ДД.ММ.РРРР, ГГ:ХХ — для показу касиру. */
export const displayDateTime = (value) => {
    const date = new Date(value);

    if (!value || Number.isNaN(date.getTime())) {
        return '—';
    }

    return new Intl.DateTimeFormat('uk-UA', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    }).format(date);
};

/** ГГ:ХХ — коли дата вже зрозуміла з контексту. */
export const displayTime = (value) => {
    const date = new Date(value);

    if (!value || Number.isNaN(date.getTime())) {
        return '—';
    }

    return new Intl.DateTimeFormat('uk-UA', { hour: '2-digit', minute: '2-digit' }).format(date);
};

/**
 * Українське відмінювання після числівника.
 * pluralize(2, ['операція', 'операції', 'операцій']) → "2 операції"
 */
export const pluralize = (count, [one, few, many]) => {
    const value = Math.abs(Number(count) || 0);
    const mod10 = value % 10;
    const mod100 = value % 100;

    if (mod10 === 1 && mod100 !== 11) {
        return `${count} ${one}`;
    }

    if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) {
        return `${count} ${few}`;
    }

    return `${count} ${many}`;
};
